import { useState, useMemo, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import { v7 as uuidv7 } from "uuid";
import { enqueue, getLocalBlob, isLocalFileId, isNetworkError } from "../lib/outbox";
import type { HospitalDto } from "@ambulance/shared";
import { useApi } from "../api";
import { useMe } from "../hooks/useMe";
import { Section, TextField } from "../components/ChecklistFormBits";
import { ImageUploader, UploadedImage } from "../components/ImageUploader";
import { SearchableSelect } from "../components/SearchableSelect";
import { SignaturePad } from "../components/SignaturePad";
import { buildInitialChecklist } from "../lib/initialChecklist";
import {
  MARK_STATES,
  NEW_CHECKLIST_SECTIONS,
  NO_EXPIRY_ITEMS,
  IMAGE_SLOTS,
  ImageSlotKey,
  isExpiryDay,
  extraKindFor,
} from "@ambulance/shared";
import {
  CheckCircle2,
  Loader2,
  Send,
  User,
  LogOut,
  RotateCcw,
  CalendarClock,
  CloudOff,
} from "lucide-react";

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]";
const labelCls =
  "block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1";

// ---------------------------------------------------------------------------
// Draft persistence — auto-save the in-progress checklist to THIS device so a
// page refresh, browser crash, or dropped connection never wipes the crew's
// work. Drafts expire after 3 h so a new shift can't inherit a stale one.
// ---------------------------------------------------------------------------
const DRAFT_KEY = "hmg-checklist-draft-v1";
const DRAFT_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours

const EMPTY_IMAGES = (): Record<ImageSlotKey, UploadedImage | null> => ({
  front: null,
  back: null,
  right: null,
  left: null,
  inside1: null,
  inside2: null,
});

type DraftShape = {
  savedAt: number;
  data: ReturnType<typeof buildInitialChecklist>;
  images: Record<ImageSlotKey, UploadedImage | null>;
  defibStrip: UploadedImage | null;
};

// Overlay a saved draft onto a fresh initial so item keys added since the draft
// was saved still exist (avoids crashes right after a form change is deployed).
function mergeDraftData(
  saved: unknown
): ReturnType<typeof buildInitialChecklist> {
  const base = buildInitialChecklist();
  const out = { ...base } as Record<string, unknown>;
  const src = (saved ?? {}) as Record<string, unknown>;
  for (const key of Object.keys(base)) {
    const bv = (base as Record<string, unknown>)[key];
    const sv = src[key];
    if (sv == null) continue;
    out[key] =
      bv && typeof bv === "object" && !Array.isArray(bv)
        ? { ...(bv as object), ...(sv as object) }
        : sv;
  }
  return out as ReturnType<typeof buildInitialChecklist>;
}

// A pristine form (only the auto-filled date set) is not worth persisting.
const EMPTY_SNAPSHOT = JSON.stringify({ ...buildInitialChecklist(), date: "" });

function draftIsMeaningful(
  data: ReturnType<typeof buildInitialChecklist>,
  images: Record<ImageSlotKey, UploadedImage | null>,
  defibStrip: UploadedImage | null
): boolean {
  if (defibStrip) return true;
  if (Object.values(images).some(Boolean)) return true;
  return JSON.stringify({ ...data, date: "" }) !== EMPTY_SNAPSHOT;
}

// Drop the ephemeral preview blob URL before persisting; it's re-fetched from
// Convex storage on restore.
const stripPreview = (img: UploadedImage | null): UploadedImage | null =>
  img ? { ...img, previewUrl: "" } : null;

function fmtAgo(ms: number): string {
  const mins = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (mins < 1) return "just now";
  if (mins === 1) return "1 minute ago";
  if (mins < 60) return `${mins} minutes ago`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min ago` : `${h} h ago`;
}

// 3-state segmented control: ✓ (green) / ✗ (red) / N/A (grey).
function MarkControl({
  value,
  onChange,
  allowNA = true,
}: {
  value: string;
  onChange: (v: string) => void;
  allowNA?: boolean;
}) {
  const states = allowNA
    ? MARK_STATES
    : MARK_STATES.filter(([s]) => s !== "NA");
  const style = (state: string, on: boolean) =>
    on
      ? state === "Y"
        ? "bg-emerald-500 text-white border-emerald-500"
        : state === "N"
          ? "bg-red-500 text-white border-red-500"
          : "bg-slate-500 text-white border-slate-500"
      : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50";
  return (
    <div className="flex gap-1 flex-shrink-0">
      {states.map(([state, label]) => (
        <button
          key={state}
          type="button"
          onClick={() => onChange(value === state ? "" : state)}
          className={`w-10 py-1.5 rounded-lg border text-xs font-bold ${style(state, value === state)}`}
          aria-label={label}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// One checklist row: label (+qty), 3-state mark, optional remarks/expiry input.
function MarkRow({
  label,
  qty,
  mark,
  extra,
  extraKind,
  onMark,
  onExtra,
  allowNA = true,
  extraPlaceholder,
  alwaysShowExtra,
  noExtra,
}: {
  label: string;
  qty?: string;
  mark: string;
  extra: string;
  extraKind: "remarks" | "expiry";
  onMark: (v: string) => void;
  onExtra: (v: string) => void;
  allowNA?: boolean;
  extraPlaceholder?: string;
  // Force the extra input to always show (e.g. the ACLS seal number).
  alwaysShowExtra?: boolean;
  // Never show any extra input (items with no expiry date / no remarks).
  noExtra?: boolean;
}) {
  // Expiry date shows (and is required) once an item is marked available (✓);
  // a remarks input shows when an item is marked not-available (✗).
  const showExtra =
    !noExtra &&
    (alwaysShowExtra ||
      !!extra ||
      (extraKind === "expiry" ? mark === "Y" : mark === "N"));
  return (
    <div className="py-2 px-2 rounded hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-slate-700 flex-1 min-w-0">
          {label}
          {qty && <span className="text-xs text-slate-400"> (×{qty})</span>}
        </span>
        <MarkControl value={mark} onChange={onMark} allowNA={allowNA} />
      </div>
      {showExtra && (
        <div className="mt-1.5 flex justify-end items-center gap-2">
          {extraKind === "expiry" && (
            <span className="text-xs text-slate-500 whitespace-nowrap">
              Nearest expiry date <span className="text-red-500">*</span>
            </span>
          )}
          <input
            type={extraKind === "expiry" ? "date" : "text"}
            value={extra}
            onChange={(e) => onExtra(e.target.value)}
            // Cap remarks so a long note can't wrap and break the printed row.
            maxLength={extraKind === "remarks" ? 35 : undefined}
            placeholder={
              extraKind === "expiry" ? undefined : extraPlaceholder ?? "Remarks…"
            }
            className="w-full sm:w-56 rounded-lg border border-slate-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--hmg-red)]"
          />
        </div>
      )}
    </div>
  );
}

export function ChecklistFormPage() {
  const auth = useAuth();
  const api = useApi();
  const { me } = useMe();
  const hospitalsQ = useQuery({ queryKey: ["hospitals"], queryFn: () => api.get<HospitalDto[]>("/api/v1/hospitals"), enabled: Boolean(api.token) });
  const ambulancesQ = useQuery({ queryKey: ["ambulances"], queryFn: () => api.get<{ id: string; number: string }[]>("/api/v1/ambulances"), enabled: Boolean(api.token) });
  // Legacy code reads `_id`; keep that shape so the form body ports unchanged.
  const hospitals = hospitalsQ.data?.map((h) => ({ ...h, _id: h.id }));
  const ambulances = ambulancesQ.data;
  // One key per submission attempt: retries after a dropped connection can never create duplicates.
  const [submissionKey, setSubmissionKey] = useState(() => uuidv7());

  const [data, setData] = useState(buildInitialChecklist());
  const [images, setImages] = useState<Record<ImageSlotKey, UploadedImage | null>>({
    front: null,
    back: null,
    right: null,
    left: null,
    inside1: null,
    inside2: null,
  });
  const [defibStrip, setDefibStrip] = useState<UploadedImage | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // --- Draft auto-save (helpers defined at module scope above) ---
  const [hydrated, setHydrated] = useState(false);
  const [restoredAt, setRestoredAt] = useState<number | null>(null);
  const [rehydrateIds, setRehydrateIds] = useState<string[]>([]);
  const rehydratedQ = useQuery({
    queryKey: ["file-urls", rehydrateIds.join(","), api.token],
    queryFn: () => api.get<{ id: string; url: string | null }[]>(`/api/v1/files/urls?ids=${rehydrateIds.join(",")}`),
    enabled: Boolean(api.token) && rehydrateIds.length > 0,
  });
  const rehydratedUrls = rehydratedQ.data;

  // Restore a saved draft once, on mount. localStorage is client-only, so this
  // restore necessarily reads and sets state after mount (SSR-safe).
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw) as DraftShape;
        if (
          draft &&
          typeof draft.savedAt === "number" &&
          Date.now() - draft.savedAt < DRAFT_TTL_MS
        ) {
          setData(mergeDraftData(draft.data));
          const imgs = draft.images ?? EMPTY_IMAGES();
          setImages(imgs);
          setDefibStrip(draft.defibStrip ?? null);
          setRestoredAt(draft.savedAt);
          const ids = [
            ...Object.values(imgs)
              .filter(Boolean)
              .map((i) => (i as UploadedImage).fileId as string),
            ...(draft.defibStrip
              ? [draft.defibStrip.fileId as string]
              : []),
          ];
          // Photos uploaded earlier get fresh URLs from the API; photos kept on the
          // device (offline) are re-read from the outbox store.
          const remote = ids.filter((id) => !isLocalFileId(id));
          if (remote.length) setRehydrateIds(remote);
          const local = ids.filter(isLocalFileId);
          if (local.length) {
            void Promise.all(local.map(async (id) => [id, await getLocalBlob(id)] as const)).then((pairs) => {
              const byId = new Map<string, string>();
              for (const [id, b] of pairs) if (b) byId.set(id, URL.createObjectURL(b.blob));
              setImages((prev) => {
                const next = { ...prev };
                (Object.keys(next) as ImageSlotKey[]).forEach((k) => {
                  const img = next[k];
                  if (img && byId.has(img.fileId)) next[k] = { ...img, previewUrl: byId.get(img.fileId)!, pending: true };
                });
                return next;
              });
              setDefibStrip((prev) => (prev && byId.has(prev.fileId) ? { ...prev, previewUrl: byId.get(prev.fileId)!, pending: true } : prev));
            });
          }
        } else {
          localStorage.removeItem(DRAFT_KEY); // expired (>3 h)
        }
      }
    } catch {
      /* corrupt or unavailable storage — ignore */
    }
    setHydrated(true);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, []);

  // Re-attach fresh preview URLs to restored photos (blob URLs die on reload).
  useEffect(() => {
    if (!rehydratedUrls || !rehydrateIds.length) return;
    const byId = new Map<string, string>();
    for (const f of rehydratedUrls) if (f.url) byId.set(f.id, f.url);
    /* eslint-disable react-hooks/set-state-in-effect */
    setImages((prev) => {
      const next = { ...prev };
      (Object.keys(next) as ImageSlotKey[]).forEach((k) => {
        const img = next[k];
        if (img && byId.has(img.fileId))
          next[k] = { ...img, previewUrl: byId.get(img.fileId)! };
      });
      return next;
    });
    setDefibStrip((prev) =>
      prev && byId.has(prev.fileId)
        ? { ...prev, previewUrl: byId.get(prev.fileId)! }
        : prev
    );
    setRehydrateIds([]);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [rehydratedUrls, rehydrateIds]);

  // Auto-save on every change, once the initial restore has run.
  useEffect(() => {
    if (!hydrated || submitted) return;
    try {
      if (draftIsMeaningful(data, images, defibStrip)) {
        const payload: DraftShape = {
          savedAt: Date.now(),
          data,
          images: Object.fromEntries(
            Object.entries(images).map(([k, v]) => [k, stripPreview(v)])
          ) as Record<ImageSlotKey, UploadedImage | null>,
          defibStrip: stripPreview(defibStrip),
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
      } else {
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch {
      /* quota exceeded / storage disabled — ignore */
    }
  }, [data, images, defibStrip, hydrated, submitted]);

  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {
      /* ignore */
    }
    setRestoredAt(null);
  };

  const discardDraft = () => {
    clearDraft();
    setData(buildInitialChecklist());
    setImages(EMPTY_IMAGES());
    setDefibStrip(null);
    setError(null);
  };

  const update = <K extends keyof typeof data>(k: K, v: (typeof data)[K]) =>
    setData((d) => ({ ...d, [k]: v }));

  const updateIn = (parent: keyof typeof data, key: string, value: string) =>
    setData((d) => ({
      ...d,
      [parent]: { ...(d[parent] as Record<string, string>), [key]: value },
    }));

  const totalImages = Object.values(images).filter(Boolean).length;
  // Total photos for the counter = 6 ambulance sides + the defib test strip.
  const totalPhotos = totalImages + (defibStrip ? 1 : 0);

  const progress = useMemo(() => {
    let total = 0;
    let marked = 0;
    for (const sec of NEW_CHECKLIST_SECTIONS) {
      const rec = data[sec.key as keyof typeof data] as Record<string, string>;
      for (const item of sec.items) {
        total++;
        if (rec[item[0]]) marked++;
      }
    }
    return { total, marked };
  }, [data]);

  // Expiry dates are only requested on the 1st of the month (the monthly
  // checklist). On any other day that column is a plain Remarks field.
  const expiryRequired = isExpiryDay(data.date);

  // A ✓ item in an expiry section needs a nearest expiry date (except the ACLS
  // seal row and the no-expiry items) — and only on an expiry day.
  const needsExpiry = (secExtra: string, k: string) =>
    expiryRequired &&
    secExtra === "expiry" &&
    k !== "medicationBagSealed" &&
    !NO_EXPIRY_ITEMS.has(k);

  // Per-section completion — drives the green "done" header state.
  const sectionDone = useMemo(() => {
    const done: Record<string, boolean> = {};
    for (const sec of NEW_CHECKLIST_SECTIONS) {
      const rec = data[sec.key as keyof typeof data] as Record<string, string>;
      const exp = data[sec.extraKey as keyof typeof data] as Record<
        string,
        string
      >;
      done[sec.key] = sec.items.every(([k]) => {
        if (!rec[k]) return false;
        if (rec[k] === "Y" && needsExpiry(sec.extra, k) && !(exp[k] ?? "").trim())
          return false;
        return true;
      });
    }
    // The ACLS section also needs its seal number.
    done.aclsItems = done.aclsItems && !!data.aclsSealNumber.trim();
    return done;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const shiftInfoDone = !!(
    data.hospitalId &&
    data.ambulanceNo &&
    data.date &&
    data.shift &&
    data.type &&
    data.level &&
    data.mileage.trim()
  );
  const handoverDone =
    !!data.handover.outgoingName.trim() &&
    !!data.handover.outgoingId.trim() &&
    !!data.handover.outgoingSignature &&
    !!data.handover.incomingName.trim() &&
    !!data.handover.incomingId.trim() &&
    !!data.handover.incomingSignature &&
    !!(data.submittedByName ?? "").trim();

  const dirty = hydrated && draftIsMeaningful(data, images, defibStrip);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!data.hospitalId) return setError("Please select a hospital / station");
    if (!data.ambulanceNo) return setError("Please select an ambulance code");
    if (!data.date) return setError("Please pick a date");
    if (!data.shift) return setError("Please select the shift (Day / Night)");
    if (!data.type) return setError("Please select the ambulance type");
    if (!data.level) return setError("Please select the level (BLS / ALS / CC)");
    if (!data.mileage.trim()) return setError("Please enter the mileage");

    // Every item must be actively marked ✓ / ✗ / N/A.
    const unmarked: string[] = [];
    for (const sec of NEW_CHECKLIST_SECTIONS) {
      const rec = data[sec.key as keyof typeof data] as Record<string, string>;
      const missing = sec.items.filter(([k]) => !rec[k]);
      if (missing.length) {
        unmarked.push(`${sec.label} (${missing.length} item${missing.length > 1 ? "s" : ""})`);
      }
    }
    if (unmarked.length) {
      return setError(
        `Please mark every item — remaining: ${unmarked.join(" · ")}`
      );
    }

    // Nearest expiry date required for every available (✓) date-bearing item.
    const missingExpiry: string[] = [];
    for (const sec of NEW_CHECKLIST_SECTIONS) {
      if (sec.extra !== "expiry") continue;
      const marks = data[sec.key as keyof typeof data] as Record<string, string>;
      const exp = data[sec.extraKey as keyof typeof data] as Record<
        string,
        string
      >;
      for (const item of sec.items) {
        const k = item[0];
        if (!needsExpiry(sec.extra, k)) continue;
        if (marks[k] === "Y" && !(exp[k] ?? "").trim())
          missingExpiry.push(item[1]);
      }
    }
    if (missingExpiry.length) {
      const shown = missingExpiry.slice(0, 6).join(", ");
      return setError(
        `Enter the nearest expiry date for: ${shown}${missingExpiry.length > 6 ? ` +${missingExpiry.length - 6} more` : ""}`
      );
    }

    // Remaining mandatory fields.
    if (!data.aclsSealNumber.trim())
      return setError("Enter the ACLS medication bag seal number");
    if (totalImages < 6)
      return setError("Please attach all 6 ambulance photos");
    if (!defibStrip)
      return setError("Please attach the defibrillator test strip photo");
    const h = data.handover;
    if (!h.outgoingName.trim()) return setError("Enter the outgoing medic name");
    if (!h.outgoingId.trim()) return setError("Enter the outgoing medic ID");
    if (!h.outgoingSignature)
      return setError("The outgoing medic signature is required");
    if (!h.incomingName.trim()) return setError("Enter the incoming medic name");
    if (!h.incomingId.trim()) return setError("Enter the incoming medic ID");
    if (!h.incomingSignature)
      return setError("The incoming medic signature is required");
    if (!(data.submittedByName ?? "").trim())
      return setError("Enter your name (submitted by)");

    setSubmitting(true);
    try {
      const imageList: { fileId: string; label: string }[] = (
        Object.entries(images) as [ImageSlotKey, UploadedImage | null][]
      )
        .filter(([, img]) => img !== null)
        .map(([slotKey, img]) => ({
          fileId: img!.fileId,
          label: IMAGE_SLOTS.find((s) => s.key === slotKey)?.label ?? slotKey,
        }));
      if (defibStrip) {
        imageList.push({
          fileId: defibStrip.fileId,
          label: "Defib Test Strip",
        });
      }

      // The device chooses the record id (UUIDv7) and the Idempotency-Key: the same
      // submission can be retried or replayed from the outbox without duplicates.
      const body = {
        ...data,
        id: submissionKey,
        shift: data.shift as "D" | "N",
        type: data.type as "I" | "II" | "III",
        level: data.level as "BLS" | "ALS" | "CC",
        images: imageList,
      };
      const localIds = imageList.map((i) => i.fileId).filter(isLocalFileId);
      const saveForLater = async () => {
        await enqueue({ id: submissionKey, kind: "checklist", path: "/api/v1/checklists", body, localFileIds: localIds, summary: `${data.ambulanceNo} · ${data.date}` });
        setSubmitted("queued");
        clearDraft();
      };
      if (localIds.length > 0 || !navigator.onLine) {
        await saveForLater();
      } else {
        try {
          const res = await api.post<{ id: string }>("/api/v1/checklists", body, { "Idempotency-Key": submissionKey });
          setSubmitted(res.id);
          clearDraft();
        } catch (e) {
          if (!isNetworkError(e)) throw e;
          await saveForLater();
        }
      }
    } catch (e: any) {
      setError(e?.message ?? "Failed to submit checklist");
    } finally {
      setSubmitting(false);
    }
  }

  // DEV ONLY — auto-fill every field (photos still need to be added manually,
  // since they require a real upload). Gated on NODE_ENV so it is stripped from
  // the production build and can never appear on the live site.
  const isDev = import.meta.env.DEV;
  function fillTest() {
    const sig =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==";
    setData((d) => {
      const next = { ...d } as Record<string, unknown>;
      next.hospitalId = hospitals?.[0]?._id ?? d.hospitalId;
      next.ambulanceNo = ambulances?.[0]?.number ?? d.ambulanceNo;
      next.date = new Date().toISOString().slice(0, 10);
      next.shift = "D";
      next.type = "I";
      next.level = "BLS";
      next.mileage = "123456";
      next.externalNote = "Test — auto-filled";
      next.aclsSealNumber = "TEST-SEAL-001";
      for (const sec of NEW_CHECKLIST_SECTIONS) {
        const marks: Record<string, string> = {};
        const extras: Record<string, string> = {};
        for (const [k] of sec.items) {
          marks[k] = "Y";
          if (sec.extra === "expiry" && !NO_EXPIRY_ITEMS.has(k))
            extras[k] = "2027-12-31";
        }
        next[sec.key] = marks;
        next[sec.extraKey] = extras;
      }
      next.handover = {
        outgoingName: "Test Outgoing",
        outgoingId: "12345",
        outgoingSignature: sig,
        incomingName: "Test Incoming",
        incomingId: "67890",
        incomingSignature: sig,
      };
      next.submittedByName = "Test User";
      return next as unknown as typeof d;
    });
  }

  if (submitted) {
    return (
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-8 max-w-md w-full text-center">
          {submitted === "queued" ? (
            <CloudOff className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          ) : (
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          )}
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {submitted === "queued" ? "Checklist saved on this device" : "Checklist submitted"}
          </h1>
          <p className="text-slate-600 mb-6">
            {submitted === "queued"
              ? "There is no connection right now. It will be sent automatically as soon as the network returns; keep this app open or installed."
              : "Thank you for checking. We wish you a quiet and safe shift."}
          </p>
          <div className="flex gap-2 flex-col">
            <button
              onClick={() => {
                setSubmitted(null);
                setSubmissionKey(uuidv7()); // a new submission needs a new id / Idempotency-Key
                setData(buildInitialChecklist());
                setImages(EMPTY_IMAGES());
                setDefibStrip(null);
                clearDraft();
              }}
              className="rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white font-semibold py-2.5 px-4"
            >
              Submit another
            </button>
            <Link
              to="/"
              className="rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 px-4"
            >
              Back to home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src="/hmg-logo.png"
              alt="HMG"
              width={140}
              height={36}
              className="h-7 sm:h-9 w-auto flex-shrink-0"
            />
            <div className="hidden md:block border-l border-slate-300 pl-3">
              <div className="text-xs uppercase tracking-wider text-[var(--hmg-red)] font-semibold">
                EMS
              </div>
              <div className="text-sm font-semibold text-slate-900">
                Ambulance Daily Checklist
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {isDev && (
              <button
                type="button"
                onClick={fillTest}
                title="Auto-fill all fields (dev/local only) — photos still manual"
                className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-amber-400 hover:bg-amber-500 text-amber-950"
              >
                Fill (test)
              </button>
            )}
            <div className="text-[10px] sm:text-xs text-slate-500 text-right">
              <div>
                {progress.marked}/{progress.total} items
              </div>
              <div>{totalPhotos}/7 photos</div>
              {dirty && (
                <div className="text-emerald-600 font-medium">
                  ✓ Saved on device
                </div>
              )}
            </div>
            {me && (
              <>
                <Link
                  to="/profile"
                  title="My Profile"
                  className="text-slate-600 hover:text-slate-900 p-2 rounded inline-flex items-center gap-1.5 text-sm"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Profile</span>
                </Link>
                <button
                  onClick={() => void auth.signoutRedirect()}
                  title="Sign out"
                  className="text-slate-600 hover:text-red-600 p-2 rounded inline-flex items-center gap-1.5 text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <form
        onSubmit={handleSubmit}
        className="flex-1 max-w-5xl mx-auto w-full px-3 sm:px-4 py-4 sm:py-6 space-y-4"
      >
        {restoredAt !== null && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-2 text-sm text-amber-900">
              <RotateCcw className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Restored your in-progress checklist (saved{" "}
                {fmtAgo(restoredAt)}). Continue where you left off, or discard it
                to start a new one.
              </span>
            </div>
            <button
              type="button"
              onClick={discardDraft}
              className="self-start sm:self-auto flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
            >
              Discard draft
            </button>
          </div>
        )}

        {/* Checklist mode notice (reacts to the selected Date). */}
        {expiryRequired ? (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 flex items-start gap-2">
            <CalendarClock className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm font-bold">Monthly checklist</div>
              <div className="text-xs text-amber-800">
                Enter the nearest expiry date for each available item.
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 flex-shrink-0 text-slate-400" />
            <div className="text-sm font-bold">Daily checklist</div>
          </div>
        )}

        {/* HEADER FIELDS */}
        <Section title="Shift Information" accent complete={shiftInfoDone}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>
                Station (Hospital) <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={data.hospitalId}
                onChange={(v) => update("hospitalId", v)}
                options={(hospitals ?? []).map((h) => ({
                  value: h._id,
                  label: h.name,
                }))}
                placeholder="Select station…"
              />
            </div>
            <div>
              <label className={labelCls}>
                Ambulance Code <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={data.ambulanceNo}
                onChange={(v) => update("ambulanceNo", v)}
                options={(ambulances ?? []).map((a) => ({
                  value: a.number,
                  label: a.number,
                }))}
                placeholder="Select ambulance…"
              />
            </div>
            <TextField
              label="Date"
              type="date"
              required
              value={data.date}
              onChange={(v) => update("date", v)}
            />
            <div>
              <label className={labelCls}>
                Shift <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                {(["D", "N"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => update("shift", s)}
                    className={`flex-1 py-2 rounded-lg border text-sm font-semibold ${data.shift === s ? "bg-[var(--hmg-red)] text-white border-[var(--hmg-red)]" : "bg-white border-slate-300 text-slate-700"}`}
                  >
                    {s === "D" ? "Day" : "Night"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>
                Ambulance Type <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                {(["I", "II", "III"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => update("type", t)}
                    className={`flex-1 py-2 rounded-lg border text-sm font-semibold ${data.type === t ? "bg-[var(--hmg-red)] text-white border-[var(--hmg-red)]" : "bg-white border-slate-300 text-slate-700"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>
                Level <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                {(["BLS", "ALS", "CC"] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => update("level", l)}
                    title={l === "CC" ? "Critical Care" : l}
                    className={`flex-1 py-2 rounded-lg border text-sm font-semibold ${data.level === l ? "bg-[var(--hmg-red)] text-white border-[var(--hmg-red)]" : "bg-white border-slate-300 text-slate-700"}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <TextField
              label="Mileage (KM)"
              required
              value={data.mileage}
              onChange={(v) => update("mileage", v)}
            />
          </div>
        </Section>

        {/* EXTERNAL — PHOTOS + NOTE */}
        <Section
          title="External — Ambulance Photos (all 6 required)"
          complete={totalImages === 6}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {IMAGE_SLOTS.map((slot) => (
              <ImageUploader
                key={slot.key}
                label={slot.label}
                value={images[slot.key]}
                onChange={(img) =>
                  setImages((prev) => ({ ...prev, [slot.key]: img }))
                }
              />
            ))}
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between">
              <label className={labelCls}>Additional Note</label>
              <span
                className={`text-xs ${
                  (data.externalNote?.length ?? 0) >= 450
                    ? "text-red-500 font-medium"
                    : "text-slate-400"
                }`}
              >
                {data.externalNote?.length ?? 0}/450
              </span>
            </div>
            <textarea
              rows={4}
              maxLength={450}
              value={data.externalNote}
              onChange={(e) => update("externalNote", e.target.value)}
              className={inputCls}
              placeholder="Any scratches, damage or remarks on the vehicle body…"
            />
          </div>
        </Section>

        {/* MARK SECTIONS */}
        {NEW_CHECKLIST_SECTIONS.map((sec) => {
          const markRec = data[sec.key as keyof typeof data] as Record<
            string,
            string
          >;
          const extraRec = data[sec.extraKey as keyof typeof data] as Record<
            string,
            string
          >;
          // Every section is Yes/No only (no N/A) with no "Mark all" shortcut.
          const allowNA = false;
          return (
            <Section
              key={sec.key}
              title={sec.label}
              complete={sectionDone[sec.key]}
            >
              <div>
                {sec.items.map((item) => {
                  const [k, label, qty] = item as unknown as [
                    string,
                    string,
                    string?,
                  ];
                  // The ACLS "Medication bag available and sealed" row carries
                  // a Seal Number (text) instead of an expiry date.
                  const isSeal =
                    sec.key === "aclsItems" && k === "medicationBagSealed";
                  return (
                    <MarkRow
                      key={k}
                      label={label}
                      qty={qty}
                      mark={markRec[k] ?? ""}
                      extra={isSeal ? data.aclsSealNumber : extraRec[k] ?? ""}
                      extraKind={
                        isSeal
                          ? "remarks"
                          : extraKindFor(sec.extra, expiryRequired)
                      }
                      extraPlaceholder={isSeal ? "Seal Number *" : undefined}
                      alwaysShowExtra={isSeal}
                      // Items with no expiry date lose the field only on the
                      // monthly form — on a daily one they still take remarks.
                      noExtra={
                        !isSeal &&
                        extraKindFor(sec.extra, expiryRequired) === "expiry" &&
                        NO_EXPIRY_ITEMS.has(k)
                      }
                      onMark={(v) =>
                        updateIn(sec.key as keyof typeof data, k, v)
                      }
                      onExtra={(v) =>
                        isSeal
                          ? update("aclsSealNumber", v)
                          : updateIn(sec.extraKey as keyof typeof data, k, v)
                      }
                      allowNA={allowNA}
                    />
                  );
                })}
              </div>
            </Section>
          );
        })}

        {/* DEFIBRILLATOR TEST STRIP */}
        <Section
          title="Defibrillator Test Strip (required)"
          complete={!!defibStrip}
        >
          <p className="text-xs text-slate-500 mb-2">
            Run the defibrillator self-test and attach a photo of the test
            strip / result.
          </p>
          <div className="max-w-xs">
            <ImageUploader
              label="Defib Test Strip"
              value={defibStrip}
              onChange={setDefibStrip}
            />
          </div>
        </Section>

        {/* SHIFT HANDOVER */}
        <Section title="Shift Handover" complete={handoverDone}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 p-3">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">
                Outgoing Medic
              </h4>
              <div className="space-y-3">
                <TextField
                  label="Name"
                  required
                  value={data.handover.outgoingName}
                  onChange={(v) => updateIn("handover", "outgoingName", v)}
                />
                <TextField
                  label="ID / Badge No"
                  required
                  value={data.handover.outgoingId}
                  onChange={(v) => updateIn("handover", "outgoingId", v)}
                />
                <div>
                  <label className={labelCls}>
                    Signature <span className="text-red-500">*</span>
                  </label>
                  <SignaturePad
                    value={data.handover.outgoingSignature}
                    onChange={(v) =>
                      updateIn("handover", "outgoingSignature", v)
                    }
                  />
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">
                Incoming Medic
              </h4>
              <div className="space-y-3">
                <TextField
                  label="Name"
                  required
                  value={data.handover.incomingName}
                  onChange={(v) => updateIn("handover", "incomingName", v)}
                />
                <TextField
                  label="ID / Badge No"
                  required
                  value={data.handover.incomingId}
                  onChange={(v) => updateIn("handover", "incomingId", v)}
                />
                <div>
                  <label className={labelCls}>
                    Signature <span className="text-red-500">*</span>
                  </label>
                  <SignaturePad
                    value={data.handover.incomingSignature}
                    onChange={(v) =>
                      updateIn("handover", "incomingSignature", v)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 max-w-sm">
            <TextField
              label="Submitted by (your name)"
              required
              value={data.submittedByName ?? ""}
              onChange={(v) => update("submittedByName", v)}
            />
          </div>
        </Section>

        <p className="text-[11px] text-slate-400">
          Marking guide: ✓ = Available and not expired · ✗ = Not available.
        </p>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 text-sm">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-4 sticky bottom-3 shadow-lg flex flex-wrap gap-3 items-center justify-between">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">
              {progress.marked}/{progress.total}
            </span>{" "}
            items marked ·{" "}
            <span className="font-semibold text-slate-900">{totalPhotos}/7</span>{" "}
            photos
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white font-semibold px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Submit Checklist
          </button>
        </div>
      </form>
    </main>
  );
}
